import type { MapRowValue } from '@sg/backend/src/lib/db/getMapOr404'
import { A, useNavigate, useSearchParams } from '@solidjs/router'
import { FaSolidPencil } from 'solid-icons/fa'
import { createEffect, createMemo, Show, Suspense } from 'solid-js'
import AlertBag from '../../components/AlertBag'
import DeleteButton from '../../components/Btn/DeleteButton'
import Loader from '../../components/Loader'
import MapShowCanvas3D from '../../components/MapShowCanvas3D'
import MapShowTable from '../../components/MapShowTable'
import mapDisplayName from '../../lib/core/state/mapDisplayName'
import { StateType } from '../../lib/core/state/state_type.enum'
import { StateTypeMap } from '../../lib/core/state/stateTypeMetaList.type'
import parseIntOrNull from '../../lib/core/util/math/parseIntOrNull'
import createRedirectIfWrongStateTypeEffect from '../../rx/effect/createRedirectIfWrongStateTypeEffect'
import createMapRowResource from '../../rx/resource/createMapRowResource'
import { createLoadingSignal, registerLoadingResource } from '../../rx/signal/create_loading_signal'
import { createAlertBagStore, registerAlertBagResource, setAlertBagWarning } from '../../rx/store/AlertBag'
import type { Nullable } from '../../typescript'

type MapShowPageSearchParams = {
  map_id: string
}

const componentStateType = StateType.Map

const MapShowPage = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams<MapShowPageSearchParams>()
  // if (!searchParams.map_id) {
  //   navigate('/maps')
  // }

  const loading = createLoadingSignal()
  const ab = createAlertBagStore()

  const stateId = createMemo<Nullable<number>>((): Nullable<number> => parseIntOrNull(searchParams.map_id))

  // eslint-disable-next-line
  const mapRowResource = createMapRowResource(stateId)
  const [mapRow] = mapRowResource
  createEffect(() => {
    const id = stateId()
    if (!id) {
      navigate('/maps')
    }
  })
  createRedirectIfWrongStateTypeEffect(componentStateType, () => mapRow()?.data?.type, () => mapRow()?.data?.online?.id)

  registerLoadingResource(loading, mapRowResource)
  registerAlertBagResource(ab, mapRowResource)

  createEffect(() => {
    const deleted_at = mapRow()?.deleted_at
    if (deleted_at) {
      setAlertBagWarning(ab, 'This map has been marked for deletion and will soon be removed by an automated garbage collection process.')
    } else {
      setAlertBagWarning(ab, null)
    }
  })

  return (
    <div class="pagec">
      <Loader signal={loading} />
      <AlertBag store={ab} />
      <Suspense fallback={<div class="text-muted">Loading...</div>}>
        <Show when={mapRow()?.data}>{(state) =>
          <>
            <h2>
              {mapDisplayName(state())}
              {' '}
              <A class="btn btn-primary btn-sm"
                href={`/map/edit?map_id=${stateId()}`}
                aria-label="edit map">
                <FaSolidPencil />
              </A>
              {' '}
              <DeleteButton
                st={StateTypeMap}
                disabled={loading()}
                ab={ab}
                item={mapRow() as NonNullable<ReturnType<typeof mapRow>>}
                onChange={(newDeletedAtValue: string | null) => {
                  console.log('onChange', 'newDeletedAtValue', newDeletedAtValue)
                }}
              />
            </h2>
            <MapShowTable row={mapRow() as MapRowValue} />
            <MapShowCanvas3D state={state()} width={512} height={512} />
          </>
        }</Show>
      </Suspense>
    </div>
  )
}

export default MapShowPage
